import { AxiosError } from 'axios';
import { z } from 'zod';

// Define error response schema with Zod
export const ApiErrorSchema = z.object({
  message: z.string().optional(),
  status: z.number().optional(),
  errors: z.record(z.string(), z.array(z.string())).optional(),
});

export type ApiErrorType = z.infer<typeof ApiErrorSchema>;

// Error categories
export const ErrorCategory = {
  NETWORK: 'network',
  AUTHENTICATION: 'authentication',
  VALIDATION: 'validation',
  SERVER: 'server',
  CLIENT: 'client',
  UNKNOWN: 'unknown',
} as const;

type ErrorCategoryType = (typeof ErrorCategory)[keyof typeof ErrorCategory];

// Normalized error structure
export interface NormalizedError {
  category: ErrorCategoryType;
  message: string;
  originalError: unknown;
  status?: number;
  validationErrors?: Record<string, string[]>;
  errorId?: string; // Could be used for error tracking services
}

/**
 * Normalizes various error types into a consistent structure
 */
export function normalizeError(error: unknown): NormalizedError {
  // Generate a unique error ID for tracking
  const errorId = generateErrorId();

  // Handle Axios errors
  if (error instanceof AxiosError) {
    const parsed = ApiErrorSchema.safeParse(error.response?.data);

    // Get HTTP status
    const status = error.response?.status;

    let category: ErrorCategoryType = ErrorCategory.UNKNOWN;
    if (!error.response) {
      category = ErrorCategory.NETWORK;
    } else if (status === 401 || status === 403) {
      category = ErrorCategory.AUTHENTICATION;
    } else if (status === 422 || status === 400) {
      category = ErrorCategory.VALIDATION;
    } else if (status && status >= 500) {
      category = ErrorCategory.SERVER;
    } else if (status && status >= 400) {
      category = ErrorCategory.CLIENT;
    }

    // If we could parse the error response, use that data
    if (parsed.success) {
      return {
        category,
        message: parsed.data.message || error.message,
        status,
        validationErrors: parsed.data.errors,
        originalError: error,
        errorId,
      };
    }

    // Fallback for unparseable error responses
    return {
      category,
      message: error.message || 'Network Error',
      status,
      originalError: error,
      errorId,
    };
  }

  // Handle standard Error objects
  if (error instanceof Error) {
    return {
      category: ErrorCategory.UNKNOWN,
      message: error.message,
      originalError: error,
      errorId,
    };
  }

  // Handle unknown errors
  return {
    category: ErrorCategory.UNKNOWN,
    message: String(error) || 'Unknown Error',
    originalError: error,
    errorId,
  };
}

// Generate a simple error ID for tracking
function generateErrorId(): string {
  return Date.now().toString(36) + Math.random().toString(36).slice(2, 7);
}
